import { useState, useEffect } from 'react';
import { CONFIG, PanelHowTo, api, useToast } from './_barrel.js';


        // Open the authenticated HTML view of a report in a new tab (bearer
        // token, so fetch + blob URL rather than a plain link).
        async function openTripReportHtml(tripId, tier, token) {
            const w = window.open('', '_blank');
            const resp = await fetch(`${CONFIG.apiBase}/api/v1/trips/${tripId}/report.html?tier=${tier}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!resp.ok) { if (w) w.close(); throw new Error(`HTTP ${resp.status}`); }
            const url = URL.createObjectURL(new Blob([await resp.text()], { type: 'text/html' }));
            if (w) w.location = url; else window.location = url;
        }

        const TRIP_CONSENT_WORDING = "During a trip Veil is managing for you, your position is also checked against the itinerary and against nearby public incident reports, so your security team is alerted. Nothing is sent to outside services, and checks stop when the trip ends or you revoke.";
        const LIVE_KIND = { trip_incident_nearby: 'Incident nearby', trip_off_itinerary: 'Off itinerary', trip_fcdo_area: 'FCDO no-travel area' };

        function TripLiveAlertsPanel({ token, trip }) {
            const [d, setD] = useState(null);
            const [radius, setRadius] = useState('');
            const [offKm, setOffKm] = useState('');
            const [err, setErr] = useState(null);
            const toast = useToast();
            const load = async () => {
                try {
                    const x = await api(`/api/v1/trips/${trip.id}/live-alerts`, {}, token);
                    setD(x); setRadius(x.incident_radius_override ?? ''); setOffKm(x.off_itinerary_override ?? '');
                } catch (e) { setErr(e.message); }
            };
            useEffect(() => { load(); }, [trip.id]);
            if (!d) return null;
            if (!d.available) return (
                <div className="panel p-6 mb-8 text-sm text-muted">Live trip alerts need a principal trip with a subject and a tracker; travel-only reports have neither.</div>
            );
            const save = async (enabled) => {
                try {
                    const body = { enabled, incident_radius_km: radius === '' ? null : parseFloat(radius), off_itinerary_km: offKm === '' ? null : parseFloat(offKm) };
                    setD(await api(`/api/v1/trips/${trip.id}/live-alerts`, { method: 'PUT', body: JSON.stringify(body) }, token));
                    toast(enabled ? 'Live alerts on' : 'Live alerts off');
                } catch (e) { setErr(e.message); }
            };
            return (
                <div className="panel p-6 mb-8">
                    <div className="flex justify-between items-center mb-2">
                        <div className="font-mono text-xs uppercase tracking-widest text-muted">Live trip alerts (Tracking)</div>
                        <span className="label-tag" style={{ color: d.enabled ? 'var(--green, #6ecb90)' : 'var(--text-muted)' }}>{d.enabled ? (d.window.active ? 'WATCHING' : 'ON · outside trip dates') : 'OFF'}</span>
                    </div>
                    <PanelHowTo id="trip_live_alerts" />
                    {err && <div className="text-[var(--red)] text-sm mb-2">{err}</div>}
                    <div className="text-sm mb-2">Trip window: {d.window.start || '?'} to {d.window.end || '?'} (UTC){!(d.window.start && d.window.end) && <span className="text-[var(--amber)]"> · set both dates or no alerts will run</span>}</div>
                    <div className="text-xs text-muted mb-2">Tracker purpose must mention travel safety. Suggested purpose (copy into the new tracker):
                        <div className="font-mono mt-1 p-2 border border-veil rounded" style={{ userSelect: 'all' }}>Travel safety during the trip: live location for the security team. {TRIP_CONSENT_WORDING}</div></div>
                    <div className="text-sm mb-3">Principal's live trackers: {d.trackers.length ? d.trackers.map(t => `${t.label || 'tracker'} (${t.status}${t.alerts_enabled ? '' : ', alerts off'})`).join('; ') : <span className="text-[var(--amber)]">none. Create one for this subject in the Tracking tab first.</span>}</div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <label className="text-xs font-mono text-muted">Incident radius km (blank = default {d.incident_radius_km})<input className="w-full bg-transparent border border-veil rounded px-2 py-1 text-sm mt-1" type="number" min="0.2" max="50" step="0.1" value={radius} onChange={e => setRadius(e.target.value)} /></label>
                        <label className="text-xs font-mono text-muted">Off-itinerary km (blank = default {d.off_itinerary_km})<input className="w-full bg-transparent border border-veil rounded px-2 py-1 text-sm mt-1" type="number" min="1" max="500" value={offKm} onChange={e => setOffKm(e.target.value)} /></label>
                    </div>
                    <div className="flex gap-3">
                        {!d.enabled && <button className="btn-gold" onClick={() => save(true)}>Switch on live alerts</button>}
                        {d.enabled && <button className="btn-gold" onClick={() => save(true)}>Save thresholds</button>}
                        {d.enabled && <button className="btn-gold" onClick={() => save(false)}>Switch off</button>}
                    </div>
                    {d.feed && <div className="text-xs text-muted font-mono mt-3">Incident feed refreshed {d.feed.refreshed_at ? new Date(d.feed.refreshed_at).toLocaleString() : 'never'} · {d.feed.incidents} geo-tagged item(s) · {Object.entries(d.feed.status || {}).map(([k, v]) => `${k}: ${v}`).join(', ')} · FCDO areas: {d.feed.fcdo_geometry}</div>}
                    {d.events.length > 0 && (
                        <div className="mt-3">{d.events.map((e, i) => (<div key={i} className="text-sm py-1 border-b border-veil"><span className="font-mono text-xs text-muted">{new Date(e.recorded_at).toLocaleString()}</span> · <b>{LIVE_KIND[e.kind] || e.kind}</b> · {e.note}{e.acknowledged ? ' (acknowledged)' : ''}</div>))}</div>
                    )}
                </div>
            );
        }

        function TripSharePanel({ token, trip }) {
            const [links, setLinks] = useState([]);
            const [tier, setTier] = useState('principal');
            const [days, setDays] = useState('7');
            const [note, setNote] = useState('');
            const [fresh, setFresh] = useState(null);
            const [err, setErr] = useState(null);
            const toast = useToast();
            const signed = trip.status === 'signed';
            const load = async () => { try { setLinks((await api(`/api/v1/trips/${trip.id}/share-links`, {}, token)).links); } catch (e) { setErr(e.message); } };
            useEffect(() => { load(); }, [trip.id]);
            const create = async () => {
                try {
                    const r = await api(`/api/v1/trips/${trip.id}/share-links`, { method: 'POST', body: JSON.stringify({ tier, days: parseInt(days, 10), recipient_note: note || null }) }, token);
                    setFresh(r); setNote(''); load();
                } catch (e) { setErr(e.message); }
            };
            const revoke = async (id) => {
                if (!confirm('Withdraw this link? It stops working immediately.')) return;
                try { await api(`/api/v1/trips/${trip.id}/share-links/${id}/revoke`, { method: 'POST' }, token); toast('Link withdrawn'); load(); } catch (e) { setErr(e.message); }
            };
            return (
                <div className="panel p-6 mb-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Share links</div>
                    <PanelHowTo id="trip_share" />
                    {err && <div className="text-[var(--red)] text-sm mb-2">{err}</div>}
                    {!signed ? <div className="text-sm text-muted">Sign off the trip to share it by link.</div> : (
                        <div className="flex flex-wrap gap-2 items-center">
                            <select className="bg-transparent border border-veil rounded px-2 py-1 text-sm" value={tier} onChange={e => setTier(e.target.value)}><option value="principal">{trip.report_kind === 'travel_only' ? 'Traveller brief' : 'Principal brief'}</option><option value="team">Team package</option></select>
                            <label className="text-xs font-mono text-muted">days <input className="w-16 bg-transparent border border-veil rounded px-2 py-1 text-sm" type="number" min="1" max="30" value={days} onChange={e => setDays(e.target.value)} /></label>
                            <input className="flex-1 bg-transparent border border-veil rounded px-2 py-1 text-sm" placeholder="sent to (e.g. principal's PA)" value={note} onChange={e => setNote(e.target.value)} />
                            <button className="btn-gold" onClick={create}>Create link</button>
                        </div>
                    )}
                    {fresh && (
                        <div className="mt-3 p-3 border border-veil rounded text-sm">
                            <div className="text-xs font-mono text-muted mb-1">Copy now: shown once</div>
                            <div className="font-mono break-all" style={{ color: 'var(--gold)' }}>{fresh.url}</div>
                            <button className="btn-gold mt-2" onClick={() => { navigator.clipboard.writeText(fresh.url); toast('Link copied'); }}>Copy link</button>
                        </div>
                    )}
                    {links.map(l => (
                        <div key={l.id} className="text-sm py-2 border-b border-veil flex justify-between gap-3">
                            <div><b>{l.tier}</b>{l.recipient_note ? ` · ${l.recipient_note}` : ''} · {l.live ? `expires ${new Date(l.expires_at).toLocaleDateString()}` : (l.revoked_at ? 'withdrawn' : 'expired')} · opened {l.open_count}×{l.last_opened_at ? `, last ${new Date(l.last_opened_at).toLocaleString()}` : ''}
                                {l.opens.length > 0 && <div className="text-xs text-muted font-mono">{l.opens.slice(0, 3).map(o => `${new Date(o.opened_at).toLocaleString()} ${o.kind} visitor ${o.visitor}`).join(' · ')}</div>}
                            </div>
                            {l.live && <button className="text-xs font-mono uppercase text-muted hover:text-[var(--red)]" onClick={() => revoke(l.id)}>Withdraw</button>}
                        </div>
                    ))}
                </div>
            );
        }

        // ---- Trip details / contacts / medical (phone-friendly forms) ----
        const inp = "w-full bg-transparent border border-veil rounded px-3 py-2 text-base mt-1";
        const lbl = "block text-xs font-mono text-muted mb-3";
        function Field({ label, value, onChange, type = 'text', ro, area, hint }) {
            return (
                <label className={lbl}>{label}{hint && <span className="text-[var(--amber)]"> · {hint}</span>}
                    {ro ? <div className="text-sm text-[var(--text)] mt-1 whitespace-pre-wrap">{value || '-'}</div>
                        : area ? <textarea className={inp} rows={2} value={value || ''} onChange={e => onChange(e.target.value)} />
                        : <input className={inp} type={type} value={value || ''} onChange={e => onChange(e.target.value)} />}
                </label>
            );
        }
        const toLocal = (iso) => iso ? String(iso).slice(0, 16) : '';
        const toUtc = (v) => v ? `${v}:00Z` : null;

        function TripDetailsPanel({ token, trip, onSaved }) {
            const ro = trip.status === 'signed';
            const pick = () => ({ start_date: trip.start_date || '', end_date: trip.end_date || '',
                arrival_flight: trip.arrival_flight || '', arrival_at: toLocal(trip.arrival_at), arrival_from: trip.arrival_from || '',
                departure_flight: trip.departure_flight || '', departure_at: toLocal(trip.departure_at), departure_to: trip.departure_to || '',
                traveller_phone: trip.traveller_phone || '' });
            const [f, setF] = useState(pick());
            const [err, setErr] = useState(null);
            const toast = useToast();
            useEffect(() => { setF(pick()); }, [trip]);
            const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
            const save = async () => {
                setErr(null);
                const body = { ...f, arrival_at: toUtc(f.arrival_at), departure_at: toUtc(f.departure_at) };
                if (!body.start_date) delete body.start_date; if (!body.end_date) delete body.end_date;
                try { await api(`/api/v1/trips/${trip.id}`, { method: 'PATCH', body: JSON.stringify(body) }, token); toast('Trip details saved'); onSaved && onSaved(); }
                catch (e) { setErr(e.message); }
            };
            return (
                <div className="panel p-4 sm:p-6 mb-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Trip details{ro ? ' (signed, read-only)' : ''}</div>
                    {err && <div className="text-[var(--red)] text-sm mb-2">{err}</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <Field ro={ro} label="Start date" type="date" value={f.start_date} onChange={set('start_date')} />
                        <Field ro={ro} label="End date" type="date" value={f.end_date} onChange={set('end_date')} />
                        <Field ro={ro} label="Arrival flight" value={f.arrival_flight} onChange={set('arrival_flight')} />
                        <Field ro={ro} label="Arrives (UTC)" type="datetime-local" value={f.arrival_at} onChange={set('arrival_at')} />
                        <Field ro={ro} label="Arriving from" value={f.arrival_from} onChange={set('arrival_from')} />
                        <Field ro={ro} label="Departure flight" value={f.departure_flight} onChange={set('departure_flight')} />
                        <Field ro={ro} label="Departs (UTC)" type="datetime-local" value={f.departure_at} onChange={set('departure_at')} />
                        <Field ro={ro} label="Departing to" value={f.departure_to} onChange={set('departure_to')} />
                        <Field ro={ro} label="Traveller phone" type="tel" hint="team report only, never shared" value={f.traveller_phone} onChange={set('traveller_phone')} />
                    </div>
                    {!ro && <button className="btn-gold w-full sm:w-auto" onClick={save}>Save trip details</button>}
                </div>
            );
        }

        const ANNEX = [
            ['family_office_contact', 'Emergency / family-office contact'], ['ops_room_contact', 'Ops room'],
            ['tl_contact', 'Team leader'], ['embassy_label', 'Embassy'], ['embassy_address', 'Embassy address', true],
            ['embassy_phone', 'Embassy phone'], ['checkin_schedule', 'Check-in schedule', true],
            ['comms_primary', 'Comms (primary)'], ['comms_fallback', 'Comms (fallback)'],
            ['rv_primary', 'RV point (primary)'], ['rv_secondary', 'RV point (secondary)'],
            ['notes_actions_on', 'Notes / actions on', true],
        ];

        function TripContactsPanel({ token, trip }) {
            const ro = trip.status === 'signed';
            const [f, setF] = useState({});
            const [err, setErr] = useState(null);
            const toast = useToast();
            useEffect(() => { (async () => { try { setF((await api(`/api/v1/trips/${trip.id}/field-annex`, {}, token)) || {}); } catch (e) { setErr(e.message); } })(); }, [trip.id]);
            const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
            const save = async () => {
                const body = {}; ANNEX.forEach(([k]) => { body[k] = f[k] || null; }); body.duress_word = f.duress_word || null;
                try { setF(await api(`/api/v1/trips/${trip.id}/field-annex`, { method: 'PUT', body: JSON.stringify(body) }, token)); toast('Contacts saved'); }
                catch (e) { setErr(e.message); }
            };
            return (
                <div className="panel p-4 sm:p-6 mb-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-3">Contacts and field plan{ro ? ' (signed, read-only)' : ''}</div>
                    {err && <div className="text-[var(--red)] text-sm mb-2">{err}</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        {ANNEX.map(([k, l, area]) => <Field key={k} ro={ro} area={area} label={l} value={f[k]} onChange={set(k)} />)}
                        <Field ro={ro} label="Duress word" hint="TEAM ONLY" value={f.duress_word} onChange={set('duress_word')} />
                    </div>
                    {!ro && <button className="btn-gold w-full sm:w-auto" onClick={save}>Save contacts</button>}
                </div>
            );
        }

        const MED = [['blood_type', 'Blood type'], ['critical_allergies', 'Critical allergies', true], ['medications', 'Medication', true],
            ['conditions', 'Conditions', true], ['emergency_medical_contact', 'Emergency medical contact'], ['consent_basis', 'Consent basis (who consented, how)', true]];

        function MedicalMember({ token, trip, member, ro }) {
            const [f, setF] = useState(null);
            const [err, setErr] = useState(null);
            const toast = useToast();
            useEffect(() => { (async () => { try { setF((await api(`/api/v1/trips/${trip.id}/party/${member.id}/medical`, {}, token)) || { include_on_card: false }); } catch (e) { setErr(e.message); } })(); }, [member.id]);
            if (!f) return null;
            const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
            const save = async () => {
                const body = { include_on_card: !!f.include_on_card }; MED.forEach(([k]) => { body[k] = f[k] || null; });
                try { setF(await api(`/api/v1/trips/${trip.id}/party/${member.id}/medical`, { method: 'PUT', body: JSON.stringify(body) }, token)); setErr(null); toast('Medical saved'); }
                catch (e) { setErr(e.message); }
            };
            return (
                <details className="border-b border-veil py-2">
                    <summary className="text-sm cursor-pointer py-1">{member.name || '(subject)'}{member.relationship ? ` · ${member.relationship}` : ''}{f.blood_type || f.critical_allergies ? ' · on file' : ''}</summary>
                    {err && <div className="text-[var(--red)] text-sm my-2">{err}</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 mt-2">
                        {MED.map(([k, l, area]) => <Field key={k} ro={ro} area={area} label={l} value={f[k]} onChange={set(k)} />)}
                    </div>
                    <label className="text-sm flex items-center gap-2 mb-3"><input type="checkbox" disabled={ro} checked={!!f.include_on_card} onChange={e => set('include_on_card')(e.target.checked)} /> Show on pocket card</label>
                    {!ro && <button className="btn-gold w-full sm:w-auto" onClick={save}>Save medical</button>}
                </details>
            );
        }

        function TripMedicalPanel({ token, trip }) {
            const ro = trip.status === 'signed';
            const party = trip.party || [];
            return (
                <div className="panel p-4 sm:p-6 mb-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-muted mb-1">Medical{ro ? ' (signed, read-only)' : ''}</div>
                    <div className="text-xs text-muted mb-2">Special-category data. Pocket card only (if ticked), never in a share link. Consent basis is required.</div>
                    {party.length ? party.map(m => <MedicalMember key={m.id} token={token} trip={trip} member={m} ro={ro} />)
                        : <div className="text-sm text-muted">Add party members first.</div>}
                </div>
            );
        }

export { TripLiveAlertsPanel, TripSharePanel, openTripReportHtml, TripDetailsPanel, TripContactsPanel, TripMedicalPanel };
